import { AdFormat } from '../config/platforms';
import { AdElement, AdScene } from '../model/types';

export type WarningSeverity = 'error' | 'warning';

export interface SceneWarning {
  id: string;
  elementId?: string;
  severity: WarningSeverity;
  message: string;
}

/** Względna luminancja wg WCAG; potrzebna do policzenia kontrastu. */
const luminance = (hex: string): number | null => {
  const match = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;

  const channels = [0, 2, 4].map((offset) => {
    const value = parseInt(match[1].slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

/** Współczynnik kontrastu WCAG (1 = brak różnicy, 21 = czerń na bieli). */
export const contrastRatio = (foreground: string, background: string): number | null => {
  const first = luminance(foreground);
  const second = luminance(background);
  if (first === null || second === null) return null;

  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
};

const centerOf = (element: AdElement): { x: number; y: number } => ({
  x: element.x + element.width / 2,
  y: element.y + element.height / 2,
});

/**
 * Kolor, który realnie leży pod tekstem.
 *
 * Szukamy najwyższego kształtu z wypełnieniem, który przykrywa środek napisu,
 * a gdy takiego nie ma, bierzemy tło planszy. Zdjęć nie oceniamy: bez analizy
 * pikseli nie wiadomo, co jest pod literami, a zgadywanie dałoby ostrzeżenia
 * gorsze niż ich brak.
 */
const colorBehind = (scene: AdScene, element: AdElement): string | null => {
  const point = centerOf(element);
  const index = scene.elements.findIndex((item) => item.id === element.id);

  for (let i = index - 1; i >= 0; i -= 1) {
    const candidate = scene.elements[i];
    if (candidate.hidden || candidate.opacity < 0.5) continue;
    if (candidate.type === 'image') return null;
    if (candidate.type !== 'shape' || !candidate.fill) continue;

    const covers = point.x >= candidate.x && point.x <= candidate.x + candidate.width
      && point.y >= candidate.y && point.y <= candidate.y + candidate.height;
    if (covers) return candidate.fill;
  }

  if (scene.background.kind === 'image') return null;
  if (scene.background.kind === 'gradient') return scene.background.gradientFrom;
  return scene.background.color;
};

/** Poniżej tej wartości napis przestaje być czytelny na małym ekranie. */
const MIN_CONTRAST = 3;

/**
 * Uwagi do kreacji sprawdzane przed pobraniem pliku.
 *
 * Świadomie nie blokują eksportu — bywają uzasadnione wyjątki, na przykład
 * napis celowo wchodzący w strefę interfejsu na roboczej wersji. Mają zwrócić
 * uwagę, a nie decydować za człowieka.
 */
export const collectWarnings = (scene: AdScene, format?: AdFormat): SceneWarning[] => {
  const warnings: SceneWarning[] = [];
  const visible = scene.elements.filter((element) => !element.hidden);

  visible.forEach((element) => {
    const outside = element.x < -1 || element.y < -1
      || element.x + element.width > scene.width + 1
      || element.y + element.height > scene.height + 1;

    if (outside) {
      warnings.push({
        id: `outside-${element.id}`,
        elementId: element.id,
        severity: 'warning',
        message: `„${element.name}" wychodzi poza planszę`,
      });
    }

    if (format?.safeArea) {
      const { top, bottom, left, right } = format.safeArea;
      const intrudes = element.y < top
        || element.y + element.height > scene.height - bottom
        || element.x < left
        || element.x + element.width > scene.width - right;

      if (intrudes) {
        warnings.push({
          id: `safe-${element.id}`,
          elementId: element.id,
          severity: 'warning',
          message: `„${element.name}" wchodzi w strefę zasłanianą przez interfejs platformy`,
        });
      }
    }

    if (element.type === 'text') {
      const behind = colorBehind(scene, element);
      const ratio = behind ? contrastRatio(element.color, behind) : null;

      if (ratio !== null && ratio < MIN_CONTRAST) {
        warnings.push({
          id: `contrast-${element.id}`,
          elementId: element.id,
          severity: 'error',
          message: `„${element.name}" ma za mały kontrast względem tła (${ratio.toFixed(1)}:1)`,
        });
      }

      if (element.autoFit && element.text.length > 0) {
        const rough = element.fontSize * element.lineHeight;
        if (element.height < rough) {
          warnings.push({
            id: `overflow-${element.id}`,
            elementId: element.id,
            severity: 'warning',
            message: `Ramka „${element.name}" jest niższa niż jeden wiersz tekstu`,
          });
        }
      }
    }
  });

  return warnings;
};
